import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Check, X, User, BookOpen, ImageIcon, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const TYPE_META = {
  lore: { icon: BookOpen, color: "text-amber-400", label: "Lore" },
  character: { icon: User, color: "text-purple-400", label: "Character" },
  fan_art: { icon: ImageIcon, color: "text-pink-400", label: "Fan Art" },
};

const AdminContributions = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const fetchPending = async () => {
    try {
      const res = await axios.get(`${API}/contributions/pending`);
      setPending(res.data);
    } catch { toast.error("Failed to fetch pending contributions"); }
    setLoading(false);
  };

  useEffect(() => { fetchPending(); }, []);

  const moderate = async (id, status, reason) => {
    try {
      await axios.put(`${API}/contributions/${id}/moderate`, { status, reason: reason || null });
      toast.success(status === "approved" ? "Contribution approved!" : "Contribution rejected");
      setPending(prev => prev.filter(c => c.id !== id));
      setRejectingId(null);
      setRejectReason("");
    } catch { toast.error("Moderation failed"); }
  };

  return (
    <AdminLayout title="Community Moderation">
      <div className="flex items-center justify-between mb-6">
        <p className="text-slate-400">{pending.length} pending contribution{pending.length !== 1 ? "s" : ""}</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="spinner"></div></div>
      ) : pending.length === 0 ? (
        <div className="card-surface p-12 text-center">
          <Check className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
          <p className="text-white font-semibold">All caught up!</p>
          <p className="text-slate-500 text-sm">No pending contributions to review</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="moderation-queue">
          {pending.map(c => {
            const meta = TYPE_META[c.type] || TYPE_META.lore;
            const Icon = meta.icon;
            return (
              <div key={c.id} className="card-surface p-5" data-testid={`pending-${c.id}`}>
                {/* Header */}
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-4 h-4 ${meta.color}`} />
                  <span className={`text-xs font-mono uppercase ${meta.color}`}>{meta.label}</span>
                  <span className="text-slate-600 text-xs">by</span>
                  <span className="text-cyan-400 text-xs font-medium">{c.author_name}</span>
                  <span className="text-slate-600 text-[10px]">{new Date(c.created_at).toLocaleDateString()}</span>
                </div>

                {/* Title & content */}
                <h3 className="text-white font-semibold font-['Outfit'] text-lg mb-2">{c.title}</h3>
                <p className="text-slate-300 text-sm whitespace-pre-line mb-3 max-h-40 overflow-y-auto">{c.content}</p>

                {/* Images */}
                {c.images?.length > 0 && (
                  <div className="flex gap-2 mb-3">
                    {c.images.map((img, i) => (
                      <img key={i} src={img} alt="" className="w-16 h-16 rounded object-cover border border-white/10" />
                    ))}
                  </div>
                )}

                {/* Character details */}
                {c.type === "character" && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {c.species && <span className="text-[10px] bg-purple-500/10 text-purple-400 px-2 py-0.5 rounded">{c.species}</span>}
                    {c.affiliation && <span className="text-[10px] bg-purple-500/10 text-purple-400 px-2 py-0.5 rounded">{c.affiliation}</span>}
                    {(c.abilities || []).map((a, i) => (
                      <span key={i} className="text-[10px] bg-white/5 text-slate-400 px-2 py-0.5 rounded">{a}</span>
                    ))}
                  </div>
                )}

                {/* Reject reason input */}
                {rejectingId === c.id && (
                  <div className="mb-3">
                    <Textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Reason for rejection (optional)..."
                      className="input-dark min-h-[60px] text-sm"
                      data-testid={`reject-reason-${c.id}`}
                    />
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    onClick={() => moderate(c.id, "approved")}
                    className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/20 gap-1"
                    size="sm"
                    data-testid={`approve-${c.id}`}
                  >
                    <Check className="w-3.5 h-3.5" /> Approve
                  </Button>
                  {rejectingId === c.id ? (
                    <>
                      <Button
                        onClick={() => moderate(c.id, "rejected", rejectReason)}
                        className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/20 gap-1"
                        size="sm"
                        data-testid={`confirm-reject-${c.id}`}
                      >
                        <X className="w-3.5 h-3.5" /> Confirm Reject
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setRejectingId(null); setRejectReason(""); }}>Cancel</Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => setRejectingId(c.id)}
                      className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/10 gap-1"
                      size="sm"
                      data-testid={`reject-${c.id}`}
                    >
                      <X className="w-3.5 h-3.5" /> Reject
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminContributions;
